/* eslint-disable react/prop-types -- this codebase does not use propTypes (see CLAUDE.md). */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeftIcon, EnvelopeIcon, TrashIcon } from "@heroicons/react/24/outline";
import Avatar from "../components/Avatar";
import TrialEndedAlert from "../components/TrialEndedAlert";
import { useLanguage } from "../contexts/LanguageContext";
import { useNotification } from "../contexts/NotificationContext";
import { useStore } from "../contexts/StoreContext";
import usePlanTier, { tierAllows } from "../hooks/usePlanTier";
import { customFieldRows, formatCustomFieldValue } from "../utils/customFields";
import { formatNumber, formatRelative } from "./dashboard/format";
import { Skeleton } from "./dashboard/components/ui";
import CustomerEditDrawer from "./customers/CustomerEditDrawer";
import { TierProgress } from "./customers/CustomerPanel";
import DeleteCustomerDialog from "./customers/DeleteCustomerDialog";
import SendPassModal from "./customers/SendPassModal";
import {
  balanceUnit,
  formatBalance,
  formatBalanceFigure,
  formatCalendarDay,
  formatDate,
  formatDateTime,
  formatDelta,
  isEditBlocked,
  kindLabelKey,
  passTypeOf,
  phoneText,
  segmentForLastVisit,
  visitStats,
  withBalanceAfter,
} from "./customers/customerFormat";
import { customerQueryKey, loadCustomer } from "./customers/loadCustomer";
import { DeltaPill, Pill, SegmentPill } from "./customers/ui";
import { buttonClass, cardClass } from "./customers/styles";

// Transactions shown at first and added per "Show older".
const TRANSACTIONS_PAGE = 20;

function BackLink() {
  const { t } = useLanguage();
  return (
    <Link
      to="/customers"
      className="inline-flex min-h-9 items-center gap-1.5 self-start rounded-lg py-0 pr-2.5 pl-1 text-sm font-medium text-gray-700 hover:text-gray-900"
    >
      <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
      {t("customers.title")}
    </Link>
  );
}

function DetailList({ rows }) {
  return (
    <dl className="m-0 grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-x-6 gap-y-[18px] text-sm">
      {rows.map(({ key, label, value }) => (
        <div key={key} className="flex min-w-0 flex-col gap-1">
          <dt className="text-[13px] text-gray-600">{label}</dt>
          <dd className="m-0 [overflow-wrap:anywhere] text-gray-900">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

export default function CustomerShow() {
  const { t, language } = useLanguage();
  const { selectedStore, canManageStore } = useStore();
  const { showNotification } = useNotification();
  const { ref } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const segmentsAllowed = tierAllows(usePlanTier(selectedStore), "insights");
  const storeId = selectedStore?.id;
  const [sendOpen, setSendOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [trialAlertOpen, setTrialAlertOpen] = useState(false);
  const [shown, setShown] = useState(TRANSACTIONS_PAGE);

  const {
    data: customer,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: customerQueryKey(storeId, ref),
    queryFn: () => loadCustomer(storeId, ref),
    enabled: !!storeId && !!ref,
  });

  // An old id link resolved: move the address to the uuid, seeding the cache
  // so the swap costs no second request.
  useEffect(() => {
    if (customer?.uuid && customer.uuid !== ref) {
      queryClient.setQueryData(customerQueryKey(storeId, customer.uuid), customer);
      navigate(`/customers/${encodeURIComponent(customer.uuid)}`, { replace: true });
    }
  }, [customer, ref, storeId, queryClient, navigate]);

  const handleSendPassClose = (result) => {
    setSendOpen(false);
    if (result?.sentTo) {
      showNotification(
        t("customers.sendPass.title"),
        t("customers.sendPass.success", { email: result.sentTo }),
        "success",
        [],
        5000,
      );
    }
  };

  const startEdit = () => {
    if (isEditBlocked(selectedStore)) {
      setTrialAlertOpen(true);
      return;
    }
    setEditOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex max-w-[1240px] flex-col gap-5" aria-busy="true">
        <BackLink />
        <span className="sr-only">{t("common.loading")}</span>
        <Skeleton className="h-16 w-80 max-w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }

  if (isError || !customer) {
    const missing = !isError || error?.response?.status === 404;
    return (
      <div className="flex max-w-[1240px] flex-col gap-5">
        <BackLink />
        <section
          role="status"
          className={`${cardClass} flex flex-col items-center gap-3 px-6 py-10 text-center`}
        >
          <h1 className="m-0 text-[22px] font-semibold text-gray-900">
            {missing ? t("customers.show.notFound.title") : t("customers.show.error")}
          </h1>
          {missing && (
            <p className="m-0 text-sm text-gray-600">{t("customers.show.notFound.body")}</p>
          )}
          <div className="flex flex-wrap justify-center gap-2">
            {!missing && (
              <button type="button" onClick={() => refetch()} className={buttonClass.secondary}>
                {t("customers.list.retry")}
              </button>
            )}
            <Link to="/customers" className={buttonClass.primary}>
              {t("customers.show.notFound.back")}
            </Link>
          </div>
        </section>
      </div>
    );
  }

  const unit = balanceUnit(passTypeOf(customer.pass));
  const money = { t, language, currency: selectedStore?.currency };
  const name = customer.name || t("customers.unnamed");
  const history = withBalanceAfter(customer.transactions);
  const newestFirst = [...history].reverse();
  const visibleRows = newestFirst.slice(0, shown);
  const { visits, rewards, lastVisitAt } = visitStats(customer.transactions);
  const now = new Date();
  const fields = customFieldRows(customer.custom_fields, selectedStore?.custom_fields);

  const details = [
    { key: "email", label: t("customers.table.email"), value: customer.email || t("customers.panel.noEmail") },
    { key: "phone", label: t("customers.table.phone"), value: phoneText(customer) || "—" },
    {
      key: "birthday",
      label: t("customers.table.birthday"),
      value: formatCalendarDay(customer.birthday, language, { day: "numeric", month: "long" }) || "—",
    },
    {
      key: "expiry",
      label: t("customers.panel.expiry"),
      value: customer.expires_at ? formatDate(customer.expires_at, language) : t("customers.panel.noExpiry"),
    },
    {
      key: "marketing",
      label: t("customers.show.marketing"),
      value: customer.is_marketing_opted_in ? t("customers.panel.agreed") : t("customers.panel.notAgreed"),
    },
    {
      key: "joined",
      label: t("customers.table.joined"),
      value: formatDate(customer.created_at, language) || "—",
    },
  ];

  return (
    <div className="flex max-w-[1240px] flex-col gap-5">
      <BackLink />

      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex min-w-0 items-center gap-4">
          <Avatar name={customer.name || customer.email} className="h-[60px] w-[60px] text-xl" />
          <div className="flex min-w-0 flex-col gap-1.5">
            <h1 className="m-0 text-2xl font-semibold tracking-tight text-gray-900 sm:text-[28px]">{name}</h1>
            <span className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
              {customer.pass?.name && <Pill>{customer.pass.name}</Pill>}
              {segmentsAllowed && <SegmentPill segment={segmentForLastVisit(lastVisitAt, now)} />}
              <span>
                {lastVisitAt
                  ? t("customers.show.lastVisit", { value: formatRelative(lastVisitAt, language, now) })
                  : t("customers.show.neverVisited")}{" "}
                {customer.created_at &&
                  t("customers.show.since", { date: formatDate(customer.created_at, language) })}
              </span>
            </span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Editing and deleting are admin-only on the API. Sending the pass
              is not: handing a customer their own card back is counter work,
              and send_pass is open to members. */}
          {canManageStore && (
            <button type="button" onClick={startEdit} className={buttonClass.secondary}>
              {t("customers.panel.edit")}
            </button>
          )}
          <button type="button" onClick={() => setSendOpen(true)} className={buttonClass.primary}>
            <EnvelopeIcon className="h-5 w-5" aria-hidden="true" />
            {t("customers.sendPass.button")}
          </button>
        </div>
      </header>

      <div className="flex flex-wrap items-stretch gap-5">
        <div className="flex min-w-0 flex-[999_1_520px] flex-col gap-5">
          <section aria-labelledby="customer-details-title" className={`${cardClass} flex flex-col gap-4 px-6 py-5`}>
            <h2 id="customer-details-title" className="m-0 text-base font-semibold text-gray-900">
              {t("customers.show.details")}
            </h2>
            <DetailList rows={details} />
          </section>

          {/* What the signup form collected beyond the standard columns,
              hidden fields (an ad click id, say) included. Read-only, so
              members see it too. */}
          {fields.length > 0 && (
            <section aria-labelledby="customer-fields-title" className={`${cardClass} flex flex-col gap-4 px-6 py-5`}>
              <div className="flex flex-col gap-1">
                <h2 id="customer-fields-title" className="m-0 text-base font-semibold text-gray-900">
                  {t("customers.show.customFields")}
                </h2>
                <span className="text-[13px] text-gray-600">{t("customers.show.customFieldsHint")}</span>
              </div>
              <DetailList
                rows={fields.map(({ key, label, value }) => ({
                  key,
                  label,
                  value: formatCustomFieldValue(value, t),
                }))}
              />
            </section>
          )}
        </div>

        <aside
          aria-label={t("customers.show.cardLabel")}
          className={`${cardClass} flex min-w-0 flex-[1_1_340px] flex-col gap-3 self-start px-5 py-[18px]`}
        >
          <h2 className="m-0 text-[15px] font-semibold text-gray-900">
            {customer.pass?.name || t("customers.table.pass")}
          </h2>
          {/* Only a stamp card with a next tier configured returns
              tier_progress, so its presence is the whole condition. */}
          {customer.tier_progress && (
            <TierProgress
              progress={customer.tier_progress}
              hideName={customer.tier_progress.current_pass_name === customer.pass?.name}
            />
          )}
          <div className="grid grid-cols-3 gap-2 pt-1">
            {[
              [t("customers.panel.visits"), formatNumber(visits, language)],
              [t("customers.panel.rewards"), formatNumber(rewards, language)],
              [t(`customers.units.${unit}.label`), formatBalanceFigure(customer.point, unit, money)],
            ].map(([label, value]) => (
              <div key={label} className="flex min-w-0 flex-col gap-0.5">
                <span className="text-xs text-gray-600">{label}</span>
                <span className="text-lg font-semibold text-gray-900 tabular-nums">{value}</span>
              </div>
            ))}
          </div>
        </aside>
      </div>

      <section aria-labelledby="customer-tx-title" className={`${cardClass} overflow-hidden`}>
        <div className="flex flex-col gap-1 px-6 py-[18px]">
          <h2 id="customer-tx-title" className="m-0 text-base font-semibold text-gray-900">
            {t("transactions.title")}
          </h2>
          <span className="text-[13px] text-gray-600">{t("customers.show.transactionsHint")}</span>
        </div>
        {history.length === 0 ? (
          <p className="m-0 border-t border-gray-100 px-6 py-8 text-center text-sm text-gray-600">
            {t("customers.show.noTransactions")}
          </p>
        ) : (
          <>
            <div className="relative overflow-x-auto">
              <table className="w-full min-w-[640px] border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th scope="col" className="px-6 py-2.5 text-xs font-semibold text-gray-600">
                      {t("transactions.table.date")}
                    </th>
                    <th scope="col" className="px-3 py-2.5 text-xs font-semibold text-gray-600">
                      {t("transactions.table.type")}
                    </th>
                    <th scope="col" className="px-3 py-2.5 text-xs font-semibold text-gray-600">
                      {t("transactions.table.user")}
                    </th>
                    <th scope="col" className="px-3 py-2.5 text-right text-xs font-semibold text-gray-600">
                      {t(`customers.units.${unit}.label`)}
                    </th>
                    <th scope="col" className="py-2.5 pr-6 pl-3 text-right text-xs font-semibold text-gray-600">
                      {t("customers.show.balanceAfter")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((tx) => {
                    const amount = Number(tx.amount) || 0;
                    return (
                      <tr key={tx.id} className="border-t border-gray-100" data-testid="transaction-row">
                        <td className="px-6 py-3 whitespace-nowrap text-gray-700 tabular-nums">
                          {formatDateTime(tx.created_at, language) || "—"}
                        </td>
                        <td className="px-3 py-3">
                          <span className="flex flex-col gap-0.5">
                            <span className="font-medium text-gray-900">{t(kindLabelKey(tx.kind))}</span>
                            {amount > 0 && (
                              <span className="text-xs text-gray-600">
                                {t("customers.show.purchase", {
                                  amount: `${selectedStore?.currency ? `${selectedStore.currency} ` : ""}${formatNumber(amount, language, { maximumFractionDigits: 2 })}`,
                                })}
                              </span>
                            )}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-gray-700">{tx.user?.name || "—"}</td>
                        <td className="px-3 py-3 text-right">
                          <DeltaPill value={Number(tx.point) || 0}>{formatDelta(tx.point, unit, money)}</DeltaPill>
                        </td>
                        <td
                          className="py-3 pr-6 pl-3 text-right font-semibold whitespace-nowrap text-gray-900 tabular-nums"
                          data-testid="balance-after"
                        >
                          {formatBalance(tx.balance_after, unit, money)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-6 py-3">
              <span role="status" className="text-[13px] text-gray-600 tabular-nums">
                {t("customers.show.txSummary", {
                  shown: formatNumber(visibleRows.length, language),
                  value: formatNumber(history.length, language),
                  count: history.length,
                })}
              </span>
              {visibleRows.length < history.length && (
                <button
                  type="button"
                  onClick={() => setShown((n) => n + TRANSACTIONS_PAGE)}
                  className={buttonClass.small}
                >
                  {t("customers.show.showOlder")}
                </button>
              )}
            </div>
          </>
        )}
      </section>

      {canManageStore && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-4">
          <span className="text-[13px] text-gray-600">{t("customers.show.deleteHint")}</span>
          <button type="button" onClick={() => setDeleteOpen(true)} className={buttonClass.dangerOutline}>
            <TrashIcon className="h-4 w-4" aria-hidden="true" />
            {t("customers.delete.confirm")}
          </button>
        </div>
      )}

      <SendPassModal isOpen={sendOpen} onClose={handleSendPassClose} customer={customer} storeId={storeId} />

      {canManageStore && (
        <>
          <CustomerEditDrawer
            open={editOpen}
            customer={customer}
            passType={passTypeOf(customer.pass)}
            onClose={() => setEditOpen(false)}
            onSaved={() => showNotification(t("customers.edit.saved"), "", "success", [], 4000)}
          />
          <DeleteCustomerDialog
            open={deleteOpen}
            customer={customer}
            storeId={storeId}
            onClose={() => setDeleteOpen(false)}
            onDeleted={() => {
              setDeleteOpen(false);
              showNotification(t("customers.delete.success", { name }), "", "success", [], 4000);
              navigate("/customers");
            }}
          />
        </>
      )}

      <TrialEndedAlert
        isOpen={trialAlertOpen}
        onClose={() => setTrialAlertOpen(false)}
        subscriptionStatus={selectedStore?.subscription_status}
      />
    </div>
  );
}
